import { Injectable } from '@angular/core';
import { catchError, finalize, lastValueFrom, map, Observable, of, timeout, tap } from 'rxjs';
import { API_ENDPOINT, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY, getApiPath, SAUCER_LOG_ACTION } from '@core/configs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LocalStorageService, SessionStorageService } from '@core/services/common';
import { DeviceLicensesService } from './device-licenses.service';
import { Mode } from '@core/constants';
import { LOG_RESULT, LOG_STATUS, LOG_TYPE, NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';
import { SaucerLogService } from '../saucer-logs/saucer-log.service';

@Injectable()
export class LoginService {
    private readonly deviceLoginTimeoutMs = 20000;

    constructor(
        private readonly http: HttpClient,
        private sessionStorageService: SessionStorageService,
        private localStorageService: LocalStorageService,
        private deviceLicensesService: DeviceLicensesService,
        private nativeDiagnosticLogService: NativeDiagnosticLogService,
        private saucerLogService: SaucerLogService
    ) { }

    login(email: string, password: string, corpPrefix: string): Observable<any> {
        const url = `${API_ENDPOINT.token.login}`;
        const logId = this.nativeDiagnosticLogService.generateLogId();
        this.nativeDiagnosticLogService.sendCareMoniLog({
            logType: LOG_TYPE.API,
            subject: getApiPath(url),
            status: LOG_STATUS.START,
            message: LOG_RESULT.OK,
            logId
        });

        // Captured by tap/catchError, written once in finalize() so END is always sent.
        let endStatus: string | number = LOG_STATUS.UNDEFINED;
        let endMessage: string = LOG_RESULT.ERROR;

        return this.http.post(url, {
            email: email,
            password: password,
            corpPrefix: corpPrefix,
        }).pipe(
            tap((response: any) => {
                endStatus = LOG_STATUS.OK;
                endMessage = LOG_RESULT.SUCCESS;
            }),
            catchError(err => {
                endStatus = err?.status ?? LOG_STATUS.UNDEFINED;
                endMessage = err?.message ? `${LOG_RESULT.ERROR} ${err.message}` : LOG_RESULT.ERROR;
                return of(err);
            }),
            finalize(() => {
                this.nativeDiagnosticLogService.sendCareMoniLog({
                    logType: LOG_TYPE.API,
                    subject: getApiPath(url),
                    status: endStatus,
                    message: endMessage,
                    logId
                });
            }),
            map((response: any) => {
                const accessToken = response?.accessToken;
                if (accessToken) {
                    const userSession = {
                        accessToken: response.accessToken,
                        refreshToken: response.refreshToken,
                    };

                    this.sessionStorageService.setUserSession(userSession)

                    delete response.accessToken;
                    delete response.refreshToken;
                    const userInf = response;
                    userInf.logInStaffId = typeof email === 'string' ? email.trim() : '';
                    this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.staffInfo, userInf);
                    if (this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) === Mode.TV) this.sessionStorageService.setLoginDateTime();
                    this.localStorageService.setItem(LOCAL_STORAGE_KEY.corpPrefix, response.corpPrefix);

                    this.saucerLogService.system(
                        {
                            apiPath: getApiPath(url),
                            content: JSON.stringify({
                                username: email,
                                loginInfo: userInf
                            })
                        },
                        {
                            action: SAUCER_LOG_ACTION.LOGIN.SUCCESS
                        }
                    );

                    return {
                        loginSuccess: true,
                        status: response.status
                    };
                }
                this.saucerLogService.error(
                    {
                        apiPath: getApiPath(url),
                        statusCode: response.status,
                        content: JSON.stringify({
                            username: email
                        })
                    },
                    {
                        action: SAUCER_LOG_ACTION.LOGIN.FAILED
                    }
                );

                return {
                    loginSuccess: false,
                    status: response.status
                };
            }));
    }
    async custLogin(custID: string, custPass: string){
        const url = `${API_ENDPOINT.token.cust}`;
        const body = {custID: custID, custPass: custPass}
        const logId = this.nativeDiagnosticLogService.generateLogId();
        this.nativeDiagnosticLogService.sendCareMoniLog({
            logType: LOG_TYPE.API,
            subject: getApiPath(url),
            status: LOG_STATUS.START,
            message: LOG_RESULT.OK,
            logId
        });
        // Captured in try/catch, written once in finally so END is always sent.
        let endStatus: string | number = LOG_STATUS.UNDEFINED;
        let endMessage: string = LOG_RESULT.ERROR;
        try {
            const res: any = await lastValueFrom(this.http.post(url, body));
            endStatus = res?.status ?? LOG_STATUS.OK;
            endMessage = LOG_RESULT.SUCCESS;
            if (res) return res;
            else return [];
        } catch (err: any) {
            endStatus = err?.status ?? LOG_STATUS.UNDEFINED;
            endMessage = err?.message ? `${LOG_RESULT.ERROR} ${err.message}` : LOG_RESULT.ERROR;
            return [];
        } finally {
            this.nativeDiagnosticLogService.sendCareMoniLog({
                logType: LOG_TYPE.API,
                subject: getApiPath(url),
                status: endStatus,
                message: endMessage,
                logId
            });
        }
    }

    /**
     * Đăng nhập bằng deviceId (TV/WebView). Trả về list org giống custLogin khi thành công.
    * Khi không có record tương ứng (hoặc lỗi) trả về [] để caller giữ nguyên màn hình login.
     */
    async loginByDeviceId(deviceId: string): Promise<any[]> {
        const headers = this.deviceLicensesService.createDeviceLoginHeaders(deviceId);
        if (!headers) {
            return [];
        }

        try {
            return await this.requestLoginByDeviceId(deviceId, headers);
        } catch (error) {
            console.error('Device auto-login failed:', error);
            return [];
        }
    }

    private async requestLoginByDeviceId(deviceId: string, headers: HttpHeaders): Promise<any[]> {
        const res: any = await lastValueFrom(
            this.http.post(
                API_ENDPOINT.token.deviceId,
                { deviceId },
                { headers },
            ).pipe(timeout({ first: this.deviceLoginTimeoutMs })),
        );

        if (res?.error === 'DEVICE_RECORD_NOT_FOUND') return [];
        return Array.isArray(res) ? res : [];
    }
}
